/* eslint-disable no-console */
import { takeLatest, put, select, call } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { push } from 'react-router-redux';
import { isEmpty } from 'lodash';
import uuidv4 from 'uuid/v4';
import moment from 'moment';
import { setNotification, operatorTyping } from 'utils';
import * as FormVersionTypes from '../types/formVersionTypes';
import { LOAD_PLANNING } from '../types/planningTypes';
import * as FormVersionApi from '../api/formVersionApi';
import { createRollOutApi, getRollsOutApi } from '../api/rollOutApi';
import { getPrepressApi } from '../api/prepressApi';
import { createJobPaletteCoatingOutApi } from '../api/coatingOutApi';
import * as FormVersionActions from '../actions/formVersionActions';
import { addNewPalette } from '../actions/jobResultActions';

const getLayout = state => state.layout;
const getFormVersion = state => state.formVersion;

function hasRollsOut(formVersion) {
    if (Array.isArray(formVersion.rolls_out)) {
        return formVersion.rolls_out.length > 0;
    }

    return formVersion.rolls_out > 0;
}

// L O A D   F O R M   V E R S I O N S
function* loadFormVersionsSaga() {
    const { sort, offset, limit, filters } = yield select(getFormVersion);
    const layout = yield select(getLayout);
    const { locale } = layout;
    try {
        const params = {
            limit,
            offset,
            joins: JSON.stringify(['form', 'rolls_outs']),
        };

        const sendFilters = {};
        let joinFilters = {};

        if (filters.machine_ids !== '') {
            sendFilters.machine_ids = filters.machine_ids;
        }

        if (filters.formVersionId !== '') {
            sendFilters.form_version_number = operatorTyping(filters.formVersionId.trim());
        }

        if (filters.formId !== '') {
            joinFilters = {
                ...joinFilters,
                'form.form_number': operatorTyping(filters.formId.trim()),
            };
        }

        if (filters.reprint !== undefined) {
            joinFilters = {
                ...joinFilters,
                'form.reprint': filters.reprint,
            };
        }

        if (filters.width !== '') {
            joinFilters = {
                ...joinFilters,
                'form.width': parseFloat(filters.width.trim()),
            };
        }

        if (filters.height !== '') {
            joinFilters = {
                ...joinFilters,
                'form.height': parseFloat(filters.height.trim()),
            };
        }

        if (filters.carpetQuality !== '') {
            joinFilters = {
                ...joinFilters,
                'form.configurations_property.name.value': operatorTyping(filters.carpetQuality.trim()),
            };
        }


        if (filters.jobId !== '') {
            sendFilters.job_numbers = operatorTyping(filters.jobId.trim());
        }


        if (filters.designId !== '') {
            joinFilters = {
                ...joinFilters,
                'form.design_id': operatorTyping(filters.designId.trim()),
            };
        }

        if (filters.orderItemId !== '') {
            joinFilters = {
                ...joinFilters,
                'form.po_number': operatorTyping(filters.orderItemId.trim()),
            };
        }

        if (filters.rollType !== undefined) {
            joinFilters = {
                ...joinFilters,
                'form.rolls_size': filters.rollType,
            };
        }

        if (filters.status !== undefined) {
            if (filters.status === 'NOT_DONE') {
                sendFilters.state = ['WAITING_FOR_MANUFACTURING', 'PLANNED', 'IN_PROGRESS'];
            } else {
                sendFilters.state = filters.status;
            }
        }

        if (filters.rollOut !== '') {
            joinFilters = {
                ...joinFilters,
                'rolls_out.rolls_out_barcode': operatorTyping(filters.rollOut.trim()),
            };
        }

        if (filters.customer !== '') {
            joinFilters = {
                ...joinFilters,
                'form.customer': operatorTyping(filters.customer.trim()),
            };
        }

        if (filters.quantity !== '') {
            sendFilters.repetition = parseInt(filters.quantity.trim(), 10);
        }

        if (filters.withBorder !== undefined) {
            joinFilters = {
                ...joinFilters,
                'form.configurations_property.code': filters.withBorder,
            };
        }

        if (filters.startDate.length > 0) {
            const customDate = [
                moment.utc(filters.startDate[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.startDate[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            sendFilters.start_date = { between: customDate };
        }

        if (filters.expectedEndDate.length > 0) {
            const customDate = [
                moment.utc(filters.expectedEndDate[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.expectedEndDate[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            sendFilters.expected_finish_date = { between: customDate };
        }

        if (!isEmpty(joinFilters)) {
            sendFilters.join = joinFilters;
        }
        params.filter = JSON.stringify(sendFilters);

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            } else if (sort.key === 'form_version_number') {
                params.sort_by = JSON.stringify({ form_version_number: sortInt });
            } else if (sort.key === 'form_number') {
                params.sort_by = JSON.stringify({ 'form.form_number': sortInt });
            } else if (sort.key === 'quality') {
                params.sort_by = JSON.stringify({ quality: sortInt });
            } else if (sort.key === 'start_date') {
                params.sort_by = JSON.stringify({ start_date: sortInt });
            } else if (sort.key === 'expected_finish_date') {
                params.sort_by = JSON.stringify({ expected_finish_date: sortInt });
            } else if (sort.key === 'shipping_date') {
                params.sort_by = JSON.stringify({ 'form.shipping_date': sortInt });
            }
        }

        const result = yield FormVersionApi.getFormVersionsApi(params);

        if (result.status === 200) {
            yield put(FormVersionActions.loadFormVersionsRes(result.data));
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(FormVersionActions.loadFormVersionsErr());
        }
    } catch (e) {
        yield put(FormVersionActions.loadFormVersionsErr());
        console.log(e);
    }
}

export function* loadFormVersionsWatcher() {
    yield takeLatest(FormVersionTypes.LOAD_FORM_VERSIONS, loadFormVersionsSaga);
}
// L O A D   F O R M   V E R S I O N S

// T O G G L E   F O R M   V E R S I O N   S T A T U S
function* toggleFormVersionStatusSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield FormVersionApi.toggleFormVersionStatusApi(action.data);

        if (result.status === 202) {
            if (action.data.step_state === 'IN_PROGRESS') {
                setNotification('success', locale, 'global.success', 'jobs.notification.startMachineSuccess');
            } else {
                setNotification('success', locale, 'global.success', 'jobs.notification.stopMachineSuccess');
            }

            if (action.load === 'planning') {
                yield put({ type: LOAD_PLANNING, data: {} });
            } else {
                yield call(loadFormVersionsSaga, {});
            }
        } else if (action.data.step_state === 'IN_PROGRESS') {
            setNotification('error', locale, 'global.error', 'formVersion.notification.startMachineError');
        } else if (action.data.step_state === 'DONE') {
            setNotification('error', locale, 'global.error', 'formVersion.notification.stopMachineError');
        }
    } catch (e) {
        console.log(e);
    }
}

export function* toggleFormVersionStatusWatcher() {
    yield takeLatest(FormVersionTypes.TOGGLE_FORM_VERSION_STATUS, toggleFormVersionStatusSaga);
}
// T O G G L E   F O R M   V E R S I O N   S T A T U S

// C R E A T E   R O L L   O U T
function* createRollOutSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield createRollOutApi(action.data);

        if (result.status === 202) {
            const resultRollOut = yield getRollsOutApi({ filter: JSON.stringify({ _id: action.data.rolls_out_id }) });
            yield put(FormVersionActions.createRollOutRes(true, false));
            yield put(FormVersionActions.createRollOutRes(false, true));
            yield put(FormVersionActions.createRollOutRes(false, false));
            setNotification('success', locale, 'global.success', 'formVersion.notification.createRollOutSuccess');

            yield put(FormVersionActions.checkedFormVersionRecords([]));

            if (action.load === 'planning') {
                yield put({ type: LOAD_PLANNING, data: {} });
            } else {
                yield call(loadFormVersionsSaga, {});
            }

            yield delay(1000);

            if (resultRollOut.status === 200) {
                const [rollOut] = resultRollOut.data.hits;
                if (rollOut) {
                    window.open(`${process.env.REACT_APP_CDN}rolls-out/${rollOut.pdf_file}`, '_blank');
                }
            }
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(FormVersionActions.createRollOutErr());
        }
    } catch (e) {
        yield put(FormVersionActions.createRollOutErr());
        setNotification('error', locale, 'global.error', 'formVersion.notification.createRollOutError');
        console.log(e);
    }
}

export function* createRollOutWatcher() {
    yield takeLatest(FormVersionTypes.CREATE_ROLL_OUT, createRollOutSaga);
}
// C R E A T E   R O L L   O U T

// C R E A T E   J O B   R E S U L T S
function* createJobResultsSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    const newPalette = { items: [], print: true };

    try {
        const params = {
            bulk: action.data,
        };

        const result = yield FormVersionApi.createJobResultsApi(params);

        if (result.status === 202) {
            yield call(loadFormVersionsSaga, {}); // make sure content on the popup also reloads

            const paletteID = uuidv4();

            newPalette.items = action.data.map(jr => (
                {
                    job_palette_id: jr.job_palette_id,
                    palette_number: paletteID,
                    order: action.data.indexOf(jr) + 1,
                }
            ));

            yield put(addNewPalette(newPalette));

            yield put(FormVersionActions.createJobResultsRes());
        } else {
            const error = yield result.json();
            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(FormVersionActions.createJobResultsErr());
        }
    } catch (e) {
        yield put(FormVersionActions.createJobResultsErr());
        setNotification('error', locale, 'global.error', 'jobResult.notification.addNewPalette.error');
        console.log(e);
    }
}

export function* createJobResultsWatcher() {
    yield takeLatest(FormVersionTypes.CREATE_JOB_RESULTS, createJobResultsSaga);
}
// C R E A T E   J O B   R E S U L T S


// R E P O R T   J O B   E R R O R
function* reportJobErrorSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield FormVersionApi.reportJobErrorApi(action.data);

        if (result.status === 202) {
            yield put(FormVersionActions.reportJobErrorRes({ closePopup: !action.data.keepPopupOpen }));

            if (action.data.caller) {
                if (action.data.caller === 'coating_out') {
                    const resultError = yield getPrepressApi({ filter: JSON.stringify({ _id: action.data.error_id, status: 'NEW' }) });

                    if (resultError.data.hits.length === 1) {
                        if (action.data.old_quantity === action.data.quantity) {
                            yield createJobPaletteCoatingOutApi({
                                job_palette_id: uuidv4(),
                                job_id: action.data.job_id,
                                quantity: action.data.quantity,
                                parent: action.data.parent,
                                delete: true,
                            });
                            yield delay(200);
                            setNotification('success', locale, 'global.success', 'formVersion.notification.reportJobError.Success');
                        }
                    } else {
                        setNotification('error', locale, 'global.error', 'coatingOut.notification.errorRejectedError');
                    }
                } else {
                    setNotification('success', locale, 'global.success', 'formVersion.notification.reportJobError.Success');
                }

                if (!action.data.keepPopupOpen) {
                    yield put(push(`/pp/${action.data.caller}/`));
                }
            } else {
                yield call(loadFormVersionsSaga, {});
                setNotification('success', locale, 'global.success', 'formVersion.notification.reportJobError.Success');
            }
        } else {
            setNotification('error', locale, 'global.error', 'formVersion.notification.reportJobError.Error');
            yield put(FormVersionActions.reportJobErrorErr());
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'formVersion.notification.reportJobError.Error');
        yield put(FormVersionActions.reportJobErrorErr());
        console.log(e);
    }
}

export function* reportJobErrorWatcher() {
    yield takeLatest(FormVersionTypes.REPORT_JOB_ERROR, reportJobErrorSaga);
}
// R E P O R T   J O B   E R R O R

// D E L E T E   F O R M   V E R S I O N
function* deleteFormVersionSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const processingFV = yield FormVersionApi.getFormVersionsApi({
            filter: JSON.stringify({ temporary: true }),
        });

        if (processingFV.data.hits.length === 0) {
            const formVersions = yield FormVersionApi.getFormVersionsApi({
                filter: JSON.stringify({ _id: action.id }),
                joins: JSON.stringify(['rolls_outs']),
                limit: action.id.length,
            });

            if (formVersions.status !== 200) {
                setNotification('error', locale, 'global.error', 'formVersion.notification.delete.error');
                yield put(FormVersionActions.deleteFormVersionErr());
                return;
            }

            if (formVersions.data.hits.some(hasRollsOut)) {
                setNotification('error', locale, 'global.error', 'formVersion.notification.delete.hasRollsOut');
                yield put(FormVersionActions.deleteFormVersionErr());
                return;
            }

            const formVersionResult = yield FormVersionApi.deleteFormVersionsApi({
                bulk: action.id.map(fv => ({ form_version_id: fv })),
            });

            if (formVersionResult.status === 202) {
                setNotification('success', locale, 'global.success', 'formVersion.notification.delete.success');
                yield put(FormVersionActions.deleteFormVersionRes(action.id));
                yield put(FormVersionActions.checkedFormVersionRecords([]));
                yield call(loadFormVersionsSaga, {});
            } else {
                setNotification('error', locale, 'global.error', 'formVersion.notification.delete.error');
                yield put(FormVersionActions.deleteFormVersionErr());
            }
        } else {
            yield delay(2000);
            yield call(deleteFormVersionSaga, action);
        }
    } catch (e) {
        yield put(FormVersionActions.deleteFormVersionErr());
        setNotification('error', locale, 'global.error', 'forms.notification.delete.error');
        console.log(e);
    }
}

export function* deleteFormVersionWatcher() {
    yield takeLatest(FormVersionTypes.DELETE_FORM_VERSION, deleteFormVersionSaga);
}
// D E L E T E   F O R M   V E R S I O N
